import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Text,
} from '@chakra-ui/react';
import './style.css';

function DailyTable({ data, rawLogs, formatDuration, parseCSTDate }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDate, setSelectedDate] = useState(null);

  // Get full day start/end based on selectedDate string like "June 10, 2025"
  const getStartAndEndOfDay = (dateStr) => {
    const dayStart = new Date(`${dateStr} 00:00:00`);
    const dayEnd = new Date(`${dateStr} 23:59:59.999`);
    return [dayStart, dayEnd];
  };

  const filteredLogs = selectedDate
    ? rawLogs
      .map(({ on, off }) => {
        if (!on || !off) return null;

        const start = new Date(on); // already CST
        const end = new Date(off);  // already CST

        const [dayStart, dayEnd] = getStartAndEndOfDay(selectedDate);

        // Must intersect the selected date
        if (end < dayStart || start > dayEnd) return null;

        // Clamp to inside the day
        const clampedStart = start < dayStart ? dayStart : start;
        const clampedEnd = end > dayEnd ? dayEnd : end;
        const duration = (clampedEnd - clampedStart) / 1000; // seconds

        return { on: clampedStart, off: clampedEnd, duration };
      })
      .filter(Boolean)
    : [];

  const totalSeconds = filteredLogs.reduce((acc, log) => acc + log.duration, 0);

  const formatSecondsDuration = (secs) => {
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    return `${hrs} hrs ${mins} mins`;
  };

  const formatSecondsDuration2 = (secs) => {
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const sec = Math.floor(secs % 60);
    return `${hrs} hrs ${mins} mins ${sec} secs`;
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour12: false });

  return (
    <>
      <Box className="daily" mt={10} w="60%" mx="auto">
        <Text fontSize="xl" mb={4} fontWeight="bold">
          Daily Summary
        </Text>
        <Table variant="striped" colorScheme="orange" size="md" cursor="pointer">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th isNumeric>Total Hours</Th>
            </Tr>
          </Thead>
         <Tbody>
  {data
    .slice() // create a copy so we don't mutate original array
    .sort((a, b) => new Date(b.date) - new Date(a.date)) // latest first
    .map(({ date }) => {
      const [dayStart, dayEnd] = getStartAndEndOfDay(date);

      const dailyLogs = rawLogs
        .map(({ on, off }) => {
          if (!on || !off) return null;

          const start = new Date(on);
          const end = new Date(off);

          if (end < dayStart || start > dayEnd) return null;

          const clampedStart = start < dayStart ? dayStart : start;
          const clampedEnd = end > dayEnd ? dayEnd : end;
          const duration = (clampedEnd - clampedStart) / 1000;

          return { duration };
        })
        .filter(Boolean);

      const dailySeconds = dailyLogs.reduce((sum, l) => sum + l.duration, 0);

      return (
        <Tr
          key={date}
          onClick={() => {
            setSelectedDate(date);
            onOpen();
          }}
          cursor="pointer"
          _hover={{
            transform: "scale(1.03)",
            transition: "transform 0.2s ease-in-out",
            fontWeight: "600",
          }}
        >
          <Td>{date}</Td>
          <Td isNumeric>{formatSecondsDuration(dailySeconds)}</Td>
        </Tr>
      );
    })}
</Tbody>


        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px) hue-rotate(40deg)" />
        <ModalContent>
          <ModalHeader>Logs for {selectedDate}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {filteredLogs.length === 0 ? (
              <Text>No logs found for this date.</Text>
            ) : (
              <>
                <Text fontWeight="medium" mb={2}>
                  Total Duration: {formatSecondsDuration2(totalSeconds)}
                </Text>
                <Table variant="striped" size="sm" mb={4}>
                  <Thead>
                    <Tr >
                      <Th>#</Th>
                      <Th>On Time</Th>
                      <Th>Off Time</Th>
                      <Th isNumeric>Duration</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredLogs.map(({ on, off, duration }, idx) => (
                      <Tr key={idx} >
                        <Td>{idx + 1}</Td>
                        <Td>{formatTime(on)}</Td>
                        <Td>{formatTime(off)}</Td>
                        <Td isNumeric>{formatSecondsDuration2(duration)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default DailyTable;
